import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star } from 'lucide-react';

const ROTATE_MS = 3000; // hold time per step
const SLIDE_MS = 700;   // slide animation duration

// Section accent colors — green for both food and retail.
const SECTION_COLORS = {
  food: '#34C759',
  retail: '#34C759',
};

// Admin's Choice — menu items flagged `isFeatured` on their
// restaurants/{id}/menuItems docs (managed from the iOS admin "Featured" tab).
// One section at a time — it follows the Food/Retail toggle on the page, so
// Food selected → Featured Food, Retail selected → Featured Retail. Cards
// auto-advance on a timer; tapping anywhere opens the business detail page.
// Desktop shows four cards per view; smaller screens show fewer.
export default function AdminsChoiceView({ foodItems = [], retailItems = [], businessType = 'food' }) {
  const [index, setIndex] = useState(0);
  const isRetail = businessType === 'retail';
  const items = isRetail ? retailItems : foodItems;

  // Auto-advance — index mods by the section's own length inside FeaturedSection.
  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setIndex(i => i + 1), ROTATE_MS);
    return () => clearInterval(t);
  }, [items.length]);

  // Fresh start each time the Food/Retail toggle flips.
  useEffect(() => { setIndex(0); }, [businessType]);

  if (items.length === 0) return null;

  return (
    <div className="pt-1">
      <FeaturedSection
        title={isRetail ? 'Featured Retail' : 'Featured Food'}
        type={businessType}
        items={items}
        index={index}
        accent={isRetail ? SECTION_COLORS.retail : SECTION_COLORS.food}
        businessType={businessType}
      />
    </div>
  );
}

// Section header (title + View All) + sliding card track + page dots.
// The track loops seamlessly: clone slides are appended after the last real
// one so the transition always moves forward, then the position snaps back
// invisibly instead of sliding all the way back through every card.
function FeaturedSection({ title, type, items, index, accent, businessType }) {
  const navigate = useNavigate();
  const len = items.length;
  const trackRef = useRef(null);
  const [slideW, setSlideW] = useState(0);
  const [virtual, setVirtual] = useState(index % len);
  const [animating, setAnimating] = useState(true);
  const lastIndex = useRef(index);

  // Max cards visible at once (lg breakpoint) — that's how many clones the
  // loop needs appended so no gap ever shows.
  const clones = len > 1 ? items.concat(items.slice(0, Math.min(4, len))) : items;

  // Measure the real slide width in px — translateX % resolves against the
  // track's own (viewport) width, not the overflow width, so px is correct
  // at every breakpoint and slide count.
  useEffect(() => {
    const measure = () => {
      const slide = trackRef.current?.firstElementChild;
      if (slide) setSlideW(slide.getBoundingClientRect().width);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [len]);

  // Step forward one card whenever the shared index ticks. virtual % len
  // stays aligned with index % len, but virtual is unbounded so the track
  // can run into the clone region without wrapping.
  useEffect(() => {
    if (index === lastIndex.current || len < 2) return;
    lastIndex.current = index;
    setVirtual(v => v + 1);
  }, [index]);

  // Once a forward step lands in the clone region, wait for the slide to
  // finish then snap back to the identical real position without animating.
  useEffect(() => {
    if (virtual < len) return;
    const t = setTimeout(() => {
      setAnimating(false);
      setVirtual(v => v % len);
    }, SLIDE_MS + 60);
    return () => clearTimeout(t);
  }, [virtual, len]);

  // Re-enable the transition after the silent snap has been applied.
  useEffect(() => {
    if (animating) return;
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => setAnimating(true))
    );
    return () => cancelAnimationFrame(raf);
  }, [animating]);

  return (
    <div className="space-y-1.5">
      {/* Header — same shape as the restaurant-type sections */}
      <div className="flex items-center gap-1.5 px-[7px]">
        <Star size={22} className="shrink-0 text-[#FFC800] fill-[#FFC800]" />
        <span className="text-[17px] font-semibold" style={{ color: accent }}>{title}</span>
        <span className="flex-1" />
        <button
          onClick={() => navigate(`/dashboard/featured?type=${type}`)}
          className="text-xs px-2 py-1 rounded-lg shrink-0 font-normal"
          style={{ color: accent, backgroundColor: `${accent}1A` }}
        >
          View All ({items.length})
        </button>
      </div>

      {/* Sliding track — 2 cards per view on mobile, 3 on md, 4 on lg+ */}
      <div className="px-[7px]">
        <div className="overflow-hidden">
          <div
            ref={trackRef}
            className="flex transition-transform ease-out"
            style={{
              transform: `translateX(-${virtual * slideW}px)`,
              transitionDuration: animating ? `${SLIDE_MS}ms` : '0ms',
            }}
          >
            {clones.map((item, i) => (
              <div key={`${item.id}-${i}`} className="w-1/2 md:w-1/3 lg:w-1/4 shrink-0 px-1">
                <FeaturedCard
                  item={item}
                  accent={accent}
                  onView={() =>
                    navigate(`/restaurant/${item.restaurantId}?businessType=${businessType || 'food'}`)
                  }
                />
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}

// Menu-card-style overlay: full-bleed image (scaled to FIT — no crop — over
// a blurred fill of itself so portrait uploads stay clean), black gradient,
// ADMIN'S CHOICE badge top-left, name + business + green price overlaid at
// the bottom. Whole card taps through to the business detail page.
function FeaturedCard({ item, accent, onView }) {
  const imageURL = item.menuItemImageURL || item.restaurantBannerURL || '';

  return (
    <button
      onClick={onView}
      className="relative w-full h-[115px] rounded-xl overflow-hidden bg-gray-900 text-left shadow-[0_2px_5px_rgba(0,0,0,0.1)] ring-1 ring-black/10"
    >
      {/* Image — fit inside the card, blurred cover behind fills the letterbox */}
      {imageURL ? (
        <>
          <img
            src={imageURL}
            alt=""
            aria-hidden
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover blur-md scale-125 opacity-80"
          />
          <img
            src={imageURL}
            alt={item.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-gray-400/30 flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}

      {/* Gradient — same as MenuItemCard: clear → black/30 → black/80 */}
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.3) 55%, transparent 100%)' }}
      />

      {/* ADMIN'S CHOICE badge */}
      <span
        className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 text-white text-[8px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-md"
        style={{ backgroundColor: accent }}
      >
        <Star size={8} className="text-[#FFC800] fill-[#FFC800]" />
        Admin's Choice
      </span>

      {/* Bottom overlay — name + business left, price right */}
      <div className="absolute inset-x-0 bottom-0 p-2 flex items-end justify-between gap-1.5">
        <div className="min-w-0">
          <h3 className="text-[11px] font-bold text-white leading-tight truncate drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
            {item.name}
          </h3>
          <span className="text-[9px] text-white/80 truncate block">
            {item.restaurantName || 'Local business'}
          </span>
        </div>
        <span className="text-[11px] font-bold text-[#30D158] shrink-0 drop-shadow-[0_1px_0_rgba(0,0,0,0.8)]">
          ${item.price?.toFixed(2) || '0.00'}
        </span>
      </div>
    </button>
  );
}
